import { normalizeProductMediaUrlForBrowser } from "@/lib/mediaUrls";

/** Resolve stored invoice/document paths to a browser-openable URL. */
export function resolveStorageDocumentUrl(filePath: string | null | undefined): string | null {
  if (!filePath) return null;
  const raw = String(filePath).trim();
  if (!raw) return null;
  const match = raw.match(/documents\/invoices\/([^/?#]+)/);
  if (match) return `/storage/documents/invoices/${match[1]}`;
  if (raw.startsWith("/storage/")) return normalizeProductMediaUrlForBrowser(raw) ?? raw;
  if (raw.startsWith("documents/")) return `/storage/${raw}`;
  return normalizeProductMediaUrlForBrowser(raw) ?? raw;
}

function toAbsoluteUrl(pathOrUrl: string): string {
  try {
    return new URL(pathOrUrl, window.location.origin).href;
  } catch {
    return pathOrUrl;
  }
}

/**
 * Open the system Print dialog for a PDF/image at a storage URL.
 * Uses a dedicated popup so Cancel / closing print never navigates away from
 * the product edit (or other) page in the original tab.
 */
export function openDocumentPrintView(
  filePathOrUrl: string,
  options?: { title?: string },
): void {
  const resolved = resolveStorageDocumentUrl(filePathOrUrl) ?? filePathOrUrl.trim();
  if (!resolved) return;
  const url = toAbsoluteUrl(resolved);
  const title = (options?.title ?? "Invoice").replace(/[<>&"]/g, "");

  // Same-origin popup (no noopener) so we can call print() and clean up.
  const w = window.open("about:blank", "_blank");
  if (!w) {
    // Popup blocked — open document in a new tab without forcing print.
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }

  w.document.open();
  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    html, body { margin: 0; height: 100%; background: #fff; }
    iframe { border: 0; width: 100%; height: 100%; display: block; }
  </style>
</head>
<body>
  <iframe id="doc" src="${url.replace(/"/g, "&quot;")}" title="${title}"></iframe>
  <script>
    (function () {
      var printed = false;
      var frame = document.getElementById("doc");
      function go() {
        if (printed) return;
        printed = true;
        try {
          if (frame && frame.contentWindow) {
            frame.contentWindow.focus();
            frame.contentWindow.print();
          } else {
            window.focus();
            window.print();
          }
        } catch (e) {
          try { window.print(); } catch (e2) {}
        }
      }
      // Print or Cancel — leave popup open with the document; never touch opener.
      window.addEventListener("afterprint", function () {});
      if (frame) {
        frame.addEventListener("load", function () { setTimeout(go, 800); });
      }
      setTimeout(go, 2500);
    })();
  </script>
</body>
</html>`);
  w.document.close();
}
